import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Loader from "../../components/common/Loader";
import Page from "../../helpers/config";
import Pagination from "../../components/common/Pagination";
import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { mkConfig, generateCsv, download } from 'export-to-csv';


const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(Page.records);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  // Modal state for date range
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

  const fetchData = async (page = currentPage, rowsPerPage = perPage, searchTerm = search) => {
    setLoader(true);
    try {
      const res = await Postdata(HttpService.admin_activity_logs, { page, limit: rowsPerPage, search: searchTerm });
      if (res.status === 200) {
        setLogs(res.data.activityLogs || []);
        setTotalRows(res.data.count || 0);
      } else {
        console.error("Failed to fetch logs");
      }
    } catch (error) {
      console.error("Error fetching logs. Please try again.", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, perPage, search]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows) => {
    setPerPage(rows);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  // Open date range modal
  const openDateRangeModal = () => {
    setShowDateRangeModal(true);
  };

  // Close date range modal
  const closeDateRangeModal = () => {
    setShowDateRangeModal(false);
  };

  const handleDateRangeExport = async () => {
    const filteredData = await fetchFilteredData();
    exportToCSV(filteredData);
    closeDateRangeModal(); // Close modal after exporting
  };

  // Fetch filtered data based on the selected date range
  const fetchFilteredData = async () => {
    setLoader(true);
    try {
      const res = await Postdata(HttpService.logs_list_with_date_range, { startDate, endDate });
      if (res.status === 200) {
        return res.data.activityLogs || [];
      }
    } catch (error) {
      console.error("Error fetching filtered data.", error);
    } finally {
      setLoader(false);
    }
    return [];
  };

  // Export data to CSV


const exportToCSV = (filteredData) => {
    console.log('filteredData::::::::: ', filteredData);

    // Check if filteredData is valid
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      alert("No data to export.");
      return;
    }

    // Define the specific fields you want to export and their custom headers
    const headers = ["#", "Action", "Description", "Entity", "Admin", "IP Address", "Created At"];
    
    // Map filteredData to include only the specific fields
    const mappedData = filteredData.map((log, idx) => ({
      "#": (currentPage - 1) * perPage + idx + 1,  // Adjust this logic if needed
      "Action": log.action,
      "Description": log.description,
      "Entity": log.entity,
      "Admin": log.admin || "-",
      "IP Address": log.ipAddress,
      "Created At": dayjs(log.createdAt).format("DD MMM YYYY, HH:mm")
    }));

    console.log('Mapped data: ', mappedData);

    // Set up the CSV export configuration
    const csvConfig = mkConfig({
      useKeysAsHeaders: true,   // Use object keys as the header titles
      headers: headers          // Custom headers based on the fields above
    });

    // Generate the CSV from the mapped data
    const csv = generateCsv(csvConfig)(mappedData);

    // Define a dynamic file name (e.g., with current date)
    const fileName = `activity_logs_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;

    // Trigger the download with the custom file name
    download(csvConfig, fileName)(csv);
};

  return (
    <div className="p-6 space-y-6 pr-0">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Activity Logs</h1>

      {/* Search bar */}
      <div className="mb-4 flex justify-end">
        <input
          type="text"
          placeholder="Search logs..."
          value={search}
          onChange={handleSearchChange}
          className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-72"
        />
      </div>

      {/* Export to CSV button */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={openDateRangeModal}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 focus:outline-none"
        >
          Export to CSV
        </button>
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow">
        <table className="min-w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
            <tr>
              <th className="px-6 py-3">#</th>
              <th className="px-6 py-3">Action</th>
              <th className="px-6 py-3">Description</th>
              <th className="px-6 py-3">Entity</th>
              <th className="px-6 py-3">Admin</th>
              <th className="px-6 py-3">IP Address</th>
              <th className="px-6 py-3">Created At</th>
            </tr>
          </thead>
          <tbody>
            {loader ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500 italic">
                  <Loader />
                </td>
              </tr>
            ) : logs.length > 0 ? (
              logs.map((log, idx) => (
                <tr key={log._id} className="border-b hover:bg-gray-50 transition">
                  <td className="px-6 py-3">{(currentPage - 1) * perPage + idx + 1}</td>
                  <td className="px-6 py-3 font-medium text-gray-900">{log.action}</td>
                  <td className="px-6 py-3">{log.description}</td>
                  <td className="px-6 py-3">{log.entity}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">{log?.admin || "-"}</td>
                  <td className="px-6 py-3">{log.ipAddress}</td>
                  <td className="px-6 py-3">{dayjs(log.createdAt).format("DD MMM YYYY, HH:mm")}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500 italic">
                  No logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalRows={totalRows}
        perPage={perPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        type="Activity Logs"
      />

      {/* Date Range Modal */}
      {showDateRangeModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-xl font-bold mb-4">Select Date Range</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeDateRangeModal}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDateRangeExport}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
